import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useStorage, type Asset } from '@/hooks/useStorage';
import { showToast } from '@/components/Toast';
import { supabase } from '@/hooks/useSupabase';

const ASSET_TYPES = [
  { key: 'all', label: 'Todos' },
  { key: 'top_banner_desktop', label: 'Banner Desktop' },
  { key: 'top_banner_mobile', label: 'Banner Mobile' },
  { key: 'main_image', label: 'Imagem Principal' },
  { key: 'pdf_cover', label: 'Capa PDF' },
  { key: 'pdf_back_cover', label: 'Contracapa PDF' },
  { key: 'footer_banner', label: 'Rodapé' },
  { key: 'logo', label: 'Logo' },
  { key: 'qr_code', label: 'QR Code' },
];

const BUCKET_CANDIDATES = ['public-assets', 'imagem', 'imagens', 'images', 'assets', 'media'];

export default function AdminLibraryPage() {
  const { listAssets, uploadImage, uploadImageByUrl, deleteAsset, replaceAsset, uploading, error } = useStorage();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [assets, setAssets] = useState<Asset[]>([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [uploadType, setUploadType] = useState<string>('top_banner_desktop');
  const [uploadCategory, setUploadCategory] = useState('');
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState('');
  const [urlName, setUrlName] = useState('');
  const [urlType, setUrlType] = useState<string>('top_banner_desktop');
  const [urlCategory, setUrlCategory] = useState('');
  const [uploadMode, setUploadMode] = useState<'file' | 'url'>('file');
  const [detectedBucket, setDetectedBucket] = useState<string | null>(null);
  const [checkingBucket, setCheckingBucket] = useState(true);

  const loadAssets = async () => {
    setLoading(true);
    const data = await listAssets(filter === 'all' ? undefined : filter);
    setAssets(data);
    setLoading(false);
  };

  useEffect(() => {
    loadAssets();
  }, [filter]);

  // Detectar bucket existente
  useEffect(() => {
    const detect = async () => {
      setCheckingBucket(true);
      for (const bucketName of BUCKET_CANDIDATES) {
        try {
          const { error: listErr } = await supabase.storage.from(bucketName).list('', { limit: 1 });
          if (!listErr || !listErr.message?.toLowerCase().includes('bucket')) {
            setDetectedBucket(bucketName);
            setCheckingBucket(false);
            return;
          }
        } catch (e) {
          // ignora
        }
      }
      setDetectedBucket(null);
      setCheckingBucket(false);
    };
    detect();
  }, []);

  const showLocalToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const asset = await uploadImage(file, uploadType, file.name, uploadCategory || undefined);
    if (asset) {
      showLocalToast('Imagem enviada com sucesso!');
      await loadAssets();
    } else {
      showLocalToast('Erro ao enviar imagem.');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUrlUpload = async () => {
    if (!urlInput.trim()) {
      showLocalToast('Digite uma URL válida.');
      return;
    }
    const name = urlName.trim() || 'Imagem URL';
    const asset = await uploadImageByUrl(urlInput.trim(), urlType, name, urlCategory || undefined);
    if (asset) {
      showLocalToast('Imagem enviada por URL com sucesso!');
      setUrlInput('');
      setUrlName('');
      await loadAssets();
    } else {
      showLocalToast('Erro ao enviar imagem por URL.');
    }
  };

  const handleCreateBucket = async () => {
    showLocalToast('Tentando criar bucket...');
    try {
      const { data, error } = await supabase.storage.createBucket('public-assets', {
        public: true,
        fileSizeLimit: 10485760,
        allowedMimeTypes: ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'],
      });
      if (!error) {
        setDetectedBucket('public-assets');
        showLocalToast('Bucket public-assets criado! Agora pode enviar imagens.');
      } else {
        showLocalToast('Erro ao criar bucket: ' + error.message);
      }
    } catch (err) {
      showLocalToast('Erro ao criar bucket: ' + (err instanceof Error ? err.message : 'Erro desconhecido'));
    }
  };

  const handleDelete = async (asset: Asset) => {
    if (window.confirm('Deseja remover esta imagem?')) {
      const ok = await deleteAsset(asset.id, asset.storage_path);
      if (ok) {
        showLocalToast('Imagem removida.');
        await loadAssets();
      } else {
        showLocalToast('Erro ao remover.');
      }
    }
  };

  const handleReplace = async (asset: Asset) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const updated = await replaceAsset(asset.id, asset.storage_path, file);
      if (updated) {
        showLocalToast('Imagem substituída.');
        await loadAssets();
      } else {
        showLocalToast('Erro ao substituir.');
      }
    };
    input.click();
  };

  const filteredAssets = assets;

  return (
    <div className="min-h-screen bg-brand-dark">
      {/* Header */}
      <header className="h-14 bg-brand-darker border-b border-brand-gold/10 flex items-center justify-between px-5 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-folder-image-line text-brand-gold text-xl" />
          </div>
          <div>
            <h1 className="text-white font-semibold text-sm">Painel Administrativo</h1>
            <p className="text-brand-text-muted text-xs">Biblioteca de Imagens</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/template"
            className="flex items-center gap-2 text-brand-text-muted hover:text-brand-gold text-sm transition-colors px-3 py-1.5 rounded-md border border-brand-gold/10 hover:border-brand-gold/30 cursor-pointer"
          >
            <i className="ri-settings-line" />
            <span className="hidden sm:inline">Template</span>
          </Link>
          <Link
            to="/admin/proposals"
            className="flex items-center gap-2 text-brand-text-muted hover:text-brand-gold text-sm transition-colors px-3 py-1.5 rounded-md border border-brand-gold/10 hover:border-brand-gold/30 cursor-pointer"
          >
            <i className="ri-file-list-3-line" />
            <span className="hidden sm:inline">Propostas</span>
          </Link>
          <Link
            to="/admin/dashboard"
            className="flex items-center gap-2 text-brand-text-muted hover:text-brand-gold text-sm transition-colors px-3 py-1.5 rounded-md border border-brand-gold/10 hover:border-brand-gold/30 cursor-pointer"
          >
            <i className="ri-dashboard-line" />
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-brand-gold hover:text-brand-gold-light text-sm transition-colors px-3 py-1.5 rounded-md border border-brand-gold/20 hover:bg-brand-gold/10 cursor-pointer"
          >
            <i className="ri-home-line" />
            <span className="hidden sm:inline">Portal</span>
          </Link>
        </div>
      </header>

      {/* Toast */}
      {toastMsg && (
        <div className="fixed top-4 right-4 z-50 bg-brand-card border border-brand-gold/30 rounded-md px-4 py-2 text-sm text-white shadow-lg">
          {toastMsg}
        </div>
      )}

      <div className="px-5 py-6 max-w-7xl mx-auto">
        {/* Bucket Status */}
        {!checkingBucket && (
          <div className={`mb-4 p-3 rounded-lg border text-sm flex items-center gap-2 ${
            detectedBucket
              ? 'bg-green-500/10 border-green-500/20 text-green-400'
              : 'bg-red-500/10 border-red-500/20 text-red-400'
          }`}>
            {detectedBucket ? (
              <>
                <i className="ri-checkbox-circle-line" />
                <span>Bucket detectado: <strong>{detectedBucket}</strong>. O upload deve funcionar normalmente.</span>
              </>
            ) : (
              <>
                <i className="ri-error-warning-line" />
                <span>Nenhum bucket de imagens encontrado. Crie um bucket no painel do Supabase (Storage &gt; New Bucket) com nome <strong>public-assets</strong> ou <strong>imagem</strong>.</span>
              </>
            )}
          </div>
        )}

        {/* Upload Section */}
        <div className="bg-brand-card/30 border border-brand-gold/10 rounded-lg p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-brand-gold text-sm font-semibold flex items-center gap-2">
              <i className="ri-upload-cloud-line" />
              Enviar Nova Imagem
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setUploadMode('file')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  uploadMode === 'file'
                    ? 'bg-brand-gold text-brand-dark'
                    : 'bg-brand-dark/50 text-brand-text-muted hover:text-white border border-brand-gold/10'
                }`}
              >
                <i className="ri-file-upload-line mr-1" />
                Arquivo
              </button>
              <button
                onClick={() => setUploadMode('url')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  uploadMode === 'url'
                    ? 'bg-brand-gold text-brand-dark'
                    : 'bg-brand-dark/50 text-brand-text-muted hover:text-white border border-brand-gold/10'
                }`}
              >
                <i className="ri-link mr-1" />
                Link URL
              </button>
            </div>
          </div>

          {/* Upload por Arquivo */}
          {uploadMode === 'file' && (
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <div className="w-full sm:w-48">
                <label className="text-brand-text-muted text-xs mb-1 block">Tipo</label>
                <select
                  value={uploadType}
                  onChange={(e) => setUploadType(e.target.value)}
                  className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
                >
                  {ASSET_TYPES.filter((t) => t.key !== 'all').map((t) => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="w-full sm:w-48">
                <label className="text-brand-text-muted text-xs mb-1 block">Categoria (opcional)</label>
                <input
                  type="text"
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm placeholder:text-brand-text-muted/60 focus:outline-none focus:border-brand-gold/50"
                  placeholder="cameras, alarmes, etc"
                />
              </div>
              <div className="flex-1" />
              <div className="w-full sm:w-auto">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-brand-gold hover:bg-brand-gold-light text-brand-dark font-medium text-sm px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-brand-dark/30 border-t-brand-dark rounded-full animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-upload-line" />
                      Selecionar e Enviar
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Upload por URL */}
          {uploadMode === 'url' && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <div className="w-full sm:w-48">
                  <label className="text-brand-text-muted text-xs mb-1 block">Tipo</label>
                  <select
                    value={urlType}
                    onChange={(e) => setUrlType(e.target.value)}
                    className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
                  >
                    {ASSET_TYPES.filter((t) => t.key !== 'all').map((t) => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="w-full sm:w-48">
                  <label className="text-brand-text-muted text-xs mb-1 block">Categoria (opcional)</label>
                  <input
                    type="text"
                    value={urlCategory}
                    onChange={(e) => setUrlCategory(e.target.value)}
                    className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm placeholder:text-brand-text-muted/60 focus:outline-none focus:border-brand-gold/50"
                    placeholder="cameras, alarmes, etc"
                  />
                </div>
                <div className="w-full sm:w-48">
                  <label className="text-brand-text-muted text-xs mb-1 block">Nome da imagem</label>
                  <input
                    type="text"
                    value={urlName}
                    onChange={(e) => setUrlName(e.target.value)}
                    className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm placeholder:text-brand-text-muted/60 focus:outline-none focus:border-brand-gold/50"
                    placeholder="Ex: Banner principal"
                  />
                </div>
                <div className="flex-1" />
                <div className="w-full sm:w-auto">
                  <button
                    onClick={handleUrlUpload}
                    disabled={uploading}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 bg-brand-gold hover:bg-brand-gold-light text-brand-dark font-medium text-sm px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {uploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-brand-dark/30 border-t-brand-dark rounded-full animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <i className="ri-link" />
                        Enviar por URL
                      </>
                    )}
                  </button>
                </div>
              </div>
              <div className="w-full">
                <label className="text-brand-text-muted text-xs mb-1 block">URL da imagem</label>
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm placeholder:text-brand-text-muted/60 focus:outline-none focus:border-brand-gold/50"
                  placeholder="https://exemplo.com/imagem.jpg"
                />
              </div>
            </div>
          )}

          {/* Erro de upload */}
          {error && (
            <div className="mt-4 flex flex-col gap-3">
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                <i className="ri-error-warning-line mr-1" />
                {error}
              </div>
              {!detectedBucket && (
                <div className="flex flex-col sm:flex-row gap-3 items-start">
                  <button
                    onClick={handleCreateBucket}
                    className="flex items-center gap-2 bg-brand-darker hover:bg-brand-dark border border-brand-gold/20 text-brand-gold text-sm px-4 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-database-2-line" />
                    Criar Bucket 'public-assets'
                  </button>
                  <button
                    onClick={() => {
                      window.open('https://supabase.com/dashboard/project/_/storage/buckets', '_blank');
                    }}
                    className="flex items-center gap-2 bg-brand-darker hover:bg-brand-dark border border-brand-gold/20 text-brand-text-muted hover:text-brand-gold text-sm px-4 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-external-link-line" />
                    Abrir Painel Supabase
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {ASSET_TYPES.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                filter === t.key
                  ? 'bg-brand-gold text-brand-dark'
                  : 'bg-brand-dark/50 text-brand-text-muted hover:text-white border border-brand-gold/10'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading && (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-gray-700 border-t-amber-500 rounded-full animate-spin mx-auto mb-3" />
            <p className="text-brand-text-muted text-sm">Carregando biblioteca...</p>
          </div>
        )}

        {!loading && filteredAssets.length === 0 && (
          <div className="text-center py-12 border border-brand-gold/10 rounded-lg">
            <div className="w-16 h-16 rounded-full border border-brand-gold/20 flex items-center justify-center mx-auto mb-4">
              <i className="ri-image-line text-brand-gold/40 text-2xl" />
            </div>
            <p className="text-brand-text-muted text-sm">Nenhuma imagem na biblioteca.</p>
            <p className="text-brand-text-muted text-xs mt-1">Use o botão acima para enviar a primeira imagem.</p>
          </div>
        )}

        {!loading && filteredAssets.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredAssets.map((asset) => (
              <div
                key={asset.id}
                className="bg-brand-card/30 border border-brand-gold/10 rounded-lg overflow-hidden hover:border-brand-gold/30 transition-colors group"
              >
                <div className="relative aspect-video bg-brand-dark/50">
                  <img
                    src={asset.public_url}
                    alt={asset.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <button
                      onClick={() => setSelectedAsset(asset)}
                      className="w-8 h-8 flex items-center justify-center rounded-md bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
                      title="Visualizar"
                    >
                      <i className="ri-eye-line text-sm" />
                    </button>
                    <button
                      onClick={() => handleReplace(asset)}
                      className="w-8 h-8 flex items-center justify-center rounded-md bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
                      title="Substituir"
                    >
                      <i className="ri-refresh-line text-sm" />
                    </button>
                    <button
                      onClick={() => handleDelete(asset)}
                      className="w-8 h-8 flex items-center justify-center rounded-md bg-white/10 hover:bg-red-500/20 text-white hover:text-red-400 transition-colors cursor-pointer"
                      title="Remover"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-brand-text text-xs font-medium truncate">{asset.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-brand-text-muted text-[10px] uppercase tracking-wide">
                      {ASSET_TYPES.find((t) => t.key === asset.type)?.label || asset.type}
                    </span>
                    {asset.category && (
                      <span className="text-brand-gold text-[10px] bg-brand-gold/10 px-1.5 py-0.5 rounded">
                        {asset.category}
                      </span>
                    )}
                  </div>
                  <p className="text-brand-text-muted text-[10px] mt-1">
                    {asset.size_bytes ? `${(asset.size_bytes / 1024).toFixed(1)} KB` : ''}
                    {' · '}
                    {new Date(asset.created_at).toLocaleDateString('pt-BR')}
                  </p>
                  {asset.preset_id && (
                    <p className="text-brand-text-muted text-[10px] mt-1">
                      Preset: {asset.preset_id}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error global */}
        {error && (
          <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Modal Preview */}
      {selectedAsset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/80" onClick={() => setSelectedAsset(null)} />
          <div className="relative bg-brand-card border border-brand-gold/20 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b border-brand-gold/10">
              <h3 className="text-white font-semibold text-sm">{selectedAsset.name}</h3>
              <button
                onClick={() => setSelectedAsset(null)}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-brand-gold/10 text-brand-text-muted hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="p-4">
              <img
                src={selectedAsset.public_url}
                alt={selectedAsset.name}
                className="w-full max-h-[60vh] object-contain rounded-md"
              />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 text-xs">
                <div>
                  <span className="text-brand-text-muted block">Tipo</span>
                  <span className="text-brand-text">
                    {ASSET_TYPES.find((t) => t.key === selectedAsset.type)?.label || selectedAsset.type}
                  </span>
                </div>
                <div>
                  <span className="text-brand-text-muted block">Categoria</span>
                  <span className="text-brand-text">{selectedAsset.category || '-'}</span>
                </div>
                <div>
                  <span className="text-brand-text-muted block">Tamanho</span>
                  <span className="text-brand-text">
                    {selectedAsset.size_bytes ? `${(selectedAsset.size_bytes / 1024).toFixed(1)} KB` : '-'}
                  </span>
                </div>
                <div>
                  <span className="text-brand-text-muted block">Data</span>
                  <span className="text-brand-text">
                    {new Date(selectedAsset.created_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>
              <div className="mt-4 p-3 bg-brand-dark/50 rounded-md">
                <span className="text-brand-text-muted text-[10px] block">URL Pública</span>
                <code className="text-brand-text text-[10px] break-all">{selectedAsset.public_url}</code>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}